/**
 * WHO CAN SIGN IN, AND WHAT AN OWNER MAY DO ABOUT IT.
 *
 * A person leaves and her record is corrected: the cleaner row is ended, her assignments stop, and
 * `api/src/db/scoped.ts` hands her nothing at all. What none of that touches is the sign-in. The
 * session cookie in her phone still resolves to a live `users` row, so she goes on being admitted
 * to a product that shows her an empty screen, and the only thing that actually shuts the door is
 * `users.status`. `docs/handover.md` section 3 item 7 names it and says where the control belongs:
 * the Crew screen, beside the people it is about.
 *
 * THE REFUSAL IS IMMEDIATE AND IT IS THE SERVER'S. `api/src/middleware/identity.ts` reads the row
 * on every request and refuses anything but `active`, so suspending somebody takes effect on her
 * next request rather than at the expiry of a session that was made long on purpose. Nothing in
 * this file enforces anything; it decides what to draw and what to say.
 *
 * SUSPENDING IS NOT DELETING AND THE WORDS SAY SO. There is no delete path for a person here and
 * there should not be: what she did stays on the audit trail with her name against it, the visits
 * she cleaned still name her, and a suspension is one column that can be set back. That is the
 * whole difference between shutting a door and rewriting history.
 *
 * AND NOBODY SUSPENDS HERSELF. It would be the last request that session ever made, from the one
 * screen that could undo it, and there is no second door back in: enrolment is a script the owner
 * runs on her own machine. So the control is not drawn on your own row at all, and the sentence
 * says why rather than leaving somebody hunting for a button that is missing.
 */
import { ROLE_LABELS, type Role, type UserStatus } from '@fk/shared';

/**
 * A person with a sign-in, as `GET /api/users` hands her over.
 *
 * `status` is optional because a read that predates the column simply does not carry it, and a
 * screen must not turn a missing field into a claim: absent is read as active, which is the
 * schema's own default, and the line then says nothing about a suspension either way.
 */
export interface SignInPerson {
  readonly id: string;
  readonly displayName: string;
  readonly role: Role;
  readonly status?: UserStatus;
}

/** What an owner may do to this person's sign-in, from this screen, right now. */
export type SignInAction = 'suspend' | 'reinstate';

export interface SignInLine {
  readonly name: string;
  /** The words beside the name: what she does here, and whether she can sign in at all. */
  readonly label: string;
  readonly suspended: boolean;
  /** True for the row belonging to the person reading the screen. */
  readonly isYou: boolean;
  /** What the state means, in a sentence somebody can act on. */
  readonly sentence: string;
  /** Null on your own row, which is the one row this screen never offers an action for. */
  readonly action: SignInAction | null;
  /** The words on the control. Null wherever `action` is. */
  readonly actionWords: string | null;
  /** The confirm step's question. Null wherever `action` is. */
  readonly question: string | null;
}

export function officeUserLabel(person: SignInPerson, meUserId: string): SignInLine {
  const role = ROLE_LABELS[person.role];
  const suspended = person.status === 'suspended';
  const isYou = person.id === meUserId;
  const name = person.displayName;

  if (isYou) {
    return {
      name,
      label: `${role}, this is you`,
      suspended,
      isYou,
      sentence:
        'This is the sign-in you are using now. Ending it would shut you out of the product from this very screen, so it is not offered here.',
      action: null,
      actionWords: null,
      question: null,
    };
  }

  if (suspended) {
    return {
      name,
      label: `${role}, suspended`,
      suspended,
      isYou,
      sentence: `${name} cannot sign in. Everything they did is on the record exactly as it was.`,
      action: 'reinstate',
      actionWords: 'Let them sign in again',
      question: `Let ${name} sign in again? They read whatever their role allows from their next sign-in, and nothing else changes.`,
    };
  }

  return {
    name,
    label: role,
    suspended,
    isYou,
    sentence: `${name} can sign in.`,
    action: 'suspend',
    actionWords: 'Suspend their sign-in',
    question: `Suspend ${name}? They cannot sign in after this, and the session they are in now stops at their next request. Nothing is removed: their record, and everything they did, stay exactly as they are, and this can be set back.`,
  };
}

/** The status a press writes, which is the opposite of the one that offered it. */
export const statusAfter = (action: SignInAction): UserStatus =>
  action === 'suspend' ? 'suspended' : 'active';

/** What was written, said back afterwards. The screen shows this over the reloaded list. */
export function signInDone(person: SignInPerson, action: SignInAction): string {
  return action === 'suspend'
    ? `${person.displayName} cannot sign in from their next request.`
    : `${person.displayName} can sign in again.`;
}

/* ------------------------------------------------------------------ *
 * Who may be given work, which is a smaller list than who has a sign-in.
 * ------------------------------------------------------------------ */

/**
 * The office, character for character as `OFFICE` in `api/src/middleware/authorize.ts`. Written
 * out rather than said as "not a cleaner", because a role added later is a role this picker should
 * stay shut to until somebody decides otherwise.
 */
const OFFICE_ROLES: readonly Role[] = ['owner', 'manager', 'office'];

/**
 * WHO A PICKER MAY OFFER, and it is not everybody the read answers with.
 *
 * `GET /api/users` answered with active office people only while a picker was the only thing
 * reading it, and both pickers leaned on that: whatever arrived was safe to draw. The Crew panel
 * needed the whole enrolment instead, cleaners and suspended accounts included, because a person
 * nobody is shown is an account nobody can reopen and the sign-in this business most needs to shut
 * belongs to a cleaner who has left. Widening the read left both pickers offering names the server
 * always refuses: `api/src/lib/office.ts` answers `not_an_office_person` at both doors that take
 * one, 422 from `POST /api/tasks` and 400 from the enquiry patch, so choosing one of those names
 * was a dead end with the name still in the box at the end of it.
 *
 * FILTERING HERE IS UX AND NEVER THE CONTROL, which is why the server keeps its own predicate and
 * this one is written to agree with it rather than to replace it. The Crew screen deliberately
 * does not use this: the whole point of that list is the rows this one takes out.
 *
 * A follow-up already given to somebody since suspended draws an empty picker, and that is the
 * right state to be shown: her name is still on the row, from the server's own join, and the box
 * under it is asking who has it now.
 */
export function officePeople<T extends SignInPerson>(people: readonly T[]): readonly T[] {
  return people.filter(
    (person) => OFFICE_ROLES.includes(person.role) && person.status !== 'suspended',
  );
}
